import { useState } from "react";
import useIsNarrow from "../hooks/useIsNarrow";

/**
 * Feature boxes mirroring StreetEasy's listing page, plus the listing description:
 *   - Home features    (unit-level; expanded by default)
 *   - Building features (building-level + policies; collapsed by default)
 *   - Listing description (collapsed by default)
 *
 * Data comes from the /predict/sale response: `property.home_features` /
 * `property.building_features` (arrays of {key, label}) and
 * `property.description` (raw text, may contain HTML). Boxes hide themselves
 * when their content is empty.
 */
export default function PropertyFeatures({ property }) {
  const homeFeatures = property?.home_features || [];
  const buildingFeatures = property?.building_features || [];
  const description = cleanDescription(property?.description);

  if (homeFeatures.length === 0 && buildingFeatures.length === 0 && !description) {
    return null;
  }

  return (
    <>
      {homeFeatures.length > 0 && (
        <FeatureBox title="Home Features" features={homeFeatures} defaultOpen={true} />
      )}
      {buildingFeatures.length > 0 && (
        <FeatureBox title="Building Features" features={buildingFeatures} defaultOpen={false} />
      )}
      {description && <DescriptionBox description={description} defaultOpen={false} />}
    </>
  );
}

// ── Shared card + collapsible header ──────────────────────────────────────

const cardStyle = (narrow) => ({
  backgroundColor: "white",
  border: "none",
  borderRadius: "8px",
  padding: narrow ? "16px 12px" : "24px",
  marginBottom: "24px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
});

function CardHeader({ title, count, open, onToggle }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: open ? "2px solid #000" : "none",
        paddingBottom: open ? "8px" : 0,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={onToggle}
    >
      <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#333", margin: 0 }}>
        {title}
        {count != null && (
          <span style={{ fontSize: "14px", fontWeight: "500", color: "#666", marginLeft: "8px" }}>
            ({count})
          </span>
        )}
      </h2>
      <span style={{ fontSize: "18px", color: "#666", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
        ▾
      </span>
    </div>
  );
}

function FeatureBox({ title, features, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const narrow = useIsNarrow();

  return (
    <div style={cardStyle(narrow)}>
      <CardHeader title={title} count={features.length} open={open} onToggle={() => setOpen((o) => !o)} />
      {open && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "8px 24px",
            marginTop: "16px",
          }}
        >
          {features.map((f) => (
            <div
              key={f.key}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 0",
                borderBottom: "1px solid #eee",
                fontSize: "14px",
                color: "#333",
              }}
            >
              <span style={{ color: "#28a745", marginRight: "8px", fontWeight: "700" }}>✓</span>
              {f.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DescriptionBox({ description, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const narrow = useIsNarrow();

  return (
    <div style={cardStyle(narrow)}>
      <CardHeader title="Listing Description" open={open} onToggle={() => setOpen((o) => !o)} />
      {open && (
        <div
          style={{
            marginTop: "16px",
            fontSize: "14px",
            lineHeight: "1.6",
            color: "#333",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Strip HTML/entities from a raw listing description into readable plain text. */
function cleanDescription(raw) {
  if (!raw || typeof raw !== "string") return "";
  return raw
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li)\s*>/gi, "\n")
    .replace(/<[^>]+>/g, "") // strip all remaining tags (incl. junk like <ROLEX>)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&(#39|rsquo|lsquo|apos);/gi, "'")
    .replace(/&(quot|ldquo|rdquo);/gi, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
